/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Scale, Clock, FileText, Download } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { LEGAL_DOCS, PLACEHOLDER_DOCS } from "@/constants/legalContent";

const Block = ({ block, docId }) => {
  switch (block.type) {
    case "subheading":
      return (
        <h3 className="font-display font-bold text-slate-900 mt-6 mb-2 text-base md:text-lg">
          {block.text}
        </h3>
      );
    case "bullets":
      return (
        <ul className="list-disc pl-5 space-y-2 marker:text-[#8B5CF6]">
          {block.items.map((item, i) => (
            <li key={i} className="text-slate-700 leading-relaxed">{item}</li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div className="overflow-x-auto my-4 rounded-xl border border-slate-200">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="bg-slate-50">
                {block.headers.map((h, i) => (
                  <th key={i} className="px-4 py-3 font-bold text-slate-900 border-b border-slate-200 align-top">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="odd:bg-white even:bg-slate-50/50">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-3 align-top border-b border-slate-100 ${ci === 0 ? "text-slate-700" : "font-semibold text-slate-900"}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "fileLink":
      return (
        <a
          href={block.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`legal-filelink-${block.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
          className="my-3 inline-flex items-center gap-2 rounded-xl border border-[#C4B5FD] bg-[#F5F3FF] px-4 py-2.5 text-sm font-bold text-[#5B21B6] transition-colors hover:bg-[#EDE9FE]"
        >
          <FileText size={16} /> {block.label}
        </a>
      );
    case "placeholderLink":
      return (
        <div
          data-testid={`legal-placeholder-${block.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
          className="my-3 inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 text-sm"
        >
          <span className="font-semibold text-slate-600">{block.label}</span>
          <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            Coming soon
          </span>
        </div>
      );
    case "p":
    default:
      return <p className="text-slate-700 leading-relaxed">{block.text}</p>;
  }
};

const LegalPage = () => {
  const location = useLocation();
  const hashId = (location.hash || "").replace("#", "");
  const initialId = LEGAL_DOCS.some((d) => d.id === hashId) ? hashId : LEGAL_DOCS[0].id;
  const [activeId, setActiveId] = useState(initialId);

  useEffect(() => {
    const id = (location.hash || "").replace("#", "");
    if (LEGAL_DOCS.some((d) => d.id === id)) {
      setActiveId(id);
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [location.hash]);

  const activeDoc = useMemo(
    () => LEGAL_DOCS.find((d) => d.id === activeId) || LEGAL_DOCS[0],
    [activeId]
  );

  const selectDoc = (id) => {
    setActiveId(id);
    window.history.replaceState(null, "", `/legal#${id}`);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="legal-page">
      <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        <Link
          to="/"
          data-testid="legal-back"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#5B21B6] mb-8"
        >
          <ArrowLeft size={16} /> Back to FinX
        </Link>

        <div className="inline-flex items-center gap-2 rounded-full bg-[#EDE9FE] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#5B21B6] mb-5">
          <Scale size={14} /> Legal &amp; Disclosures
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-900 mb-3">
          FinX <span className="brand-gradient-text">Legal Center</span>
        </h1>
        <p className="text-slate-600 max-w-2xl text-sm md:text-base leading-relaxed">
          Our regulatory disclosures, privacy practices, and the terms that govern your
          use of FinX. Select a document below to read the full text.
        </p>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Sidebar nav */}
          <nav data-testid="legal-doc-nav" className="lg:col-span-4 xl:col-span-3">
            <div className="lg:sticky lg:top-8 flex flex-col gap-2">
              {LEGAL_DOCS.map((doc) => {
                const active = doc.id === activeId;
                return (
                  <button
                    key={doc.id}
                    data-testid={`legal-nav-${doc.id}`}
                    onClick={() => selectDoc(doc.id)}
                    className={`text-left rounded-xl px-4 py-3 text-sm font-semibold transition-colors border ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#C4B5FD] hover:text-[#5B21B6]"
                    }`}
                  >
                    {doc.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Document content */}
          <article
            key={activeDoc.id}
            data-testid="legal-doc-content"
            className="lg:col-span-8 xl:col-span-9 bg-white rounded-2xl border border-slate-200 p-6 md:p-10"
          >
            <h2 data-testid="legal-doc-title" className="font-display text-2xl md:text-3xl font-black text-slate-900">
              {activeDoc.title}
            </h2>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400">
              <Clock size={13} /> {activeDoc.dateLabel || "Last updated"}: {activeDoc.lastUpdated}
            </div>

            {activeDoc.downloadHref && (
              <div className="mt-4">
                <a
                  href={activeDoc.downloadHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="legal-doc-download"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-700"
                >
                  <Download size={16} /> Download PDF
                </a>
              </div>
            )}

            {activeDoc.intro?.length > 0 && (
              <div className="mt-6 space-y-4 text-slate-700 leading-relaxed border-b border-slate-100 pb-6">
                {activeDoc.intro.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}

            <div className="mt-8 space-y-10">
              {activeDoc.sections.map((section, si) => (
                <section key={si} data-testid={`legal-section-${activeDoc.id}-${si}`}>
                  <h3 className="font-display text-lg md:text-xl font-black text-slate-900 mb-3">
                    {section.heading}
                  </h3>
                  <div className="space-y-3">
                    {section.blocks.map((block, bi) => (
                      <Block key={bi} block={block} docId={activeDoc.id} />
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {activeDoc.id === "disclosures" && (
              <p className="mt-10 pt-6 border-t border-slate-100 text-xs text-slate-400 leading-relaxed">
                The following documents are referenced above and will be made available
                here: {PLACEHOLDER_DOCS.join(", ")}.
              </p>
            )}
          </article>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-100">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#5B21B6]">
            <ArrowLeft size={16} /> Back to FinX
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
